import React, { useCallback, useState, useMemo, useEffect } from 'react';
import classNames                                           from 'classnames';
import * as PropTypes                                       from 'prop-types';
import DV                                                   from 'ts-data-validator';

import Dropdown         from '../../fields/dropdown';
import RenderButton     from '../../buttons/base/RenderButton';
import useEventListener from '../../../../common/hooks/useEventListener';

const BaseTabsWithDropDown = (props) => {
  const {
          active,
          button,
          changeTab,
          children,
          className,
          disabled,
          dropdown,
          nav,
          title,
        } = props;

  const [visibleRange, setVisibleRange] = useState({
    start: 0,
    end:   2,
  });
  const [isMobile, setIsMobile]         = useState(false);

  const handleResize = useCallback(() => {
    const { screen: { width } } = window;
    if (width <= 990) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, []);

    useEventListener('resize', handleResize);

  const handleChangeTab = useCallback((index) => {
    if (disabled !== index) {
      changeTab(index);
    }
  }, [disabled, changeTab]);

  const handleTabClick = useCallback((index) => {
    if (index === 0) {
      setVisibleRange({
        start: index,
        end:   2,
      });
    } else if (index >= nav.length - 1) {
      setVisibleRange({
        start: index - 1,
        end:   index + 1,
      });
    } else if (index > visibleRange.start) {
      setVisibleRange({
        start: index,
        end:   index + 2,
      });
    } else if (index < visibleRange.end) {
      setVisibleRange({
        start: index - 1,
        end:   index + 1,
      });
    }
    handleChangeTab(index);
  }, [nav.length, visibleRange, handleChangeTab]);

  const visibleTabs = useMemo(() => {
    return isMobile
      ? Object.values(nav)
        .slice(visibleRange.start, visibleRange.end)
      : Object.values(nav);
  }, [nav, visibleRange, isMobile]);

  useEffect(() => {
      handleResize();
  }, []);

  return (
    <div className={'tabbable'}>
      <div className={'top'} style={{ justifyContent: dropdown.options.length > 1 ? 'space-between' : 'flex-end' }}>
        {typeof title === 'string' && (
          <h2>{title}</h2>)}
        {dropdown.options.length > 1 && (
          <div>
            <Dropdown {...dropdown} />
            {typeof button === 'object' && (
              <RenderButton
                label={button.label}
                action={button.action}
              />
            )}
          </div>
        )}
        <ul className={classNames('tabs', className)}>
          {visibleTabs && visibleTabs.map((tab, index) => (
            <li
              key={index}
              className={classNames({
                'active': active === (
                  isMobile ? (
                    index + visibleRange.start) : index),
              })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (isMobile) {
                  handleTabClick(index + visibleRange.start);
                } else {
                  handleTabClick(index);
                }
              }}
            >{tab.label}</li>
          ))}
        </ul>
      </div>
      {children && (
        <div className={'tab-content'}>
          {Object.values(children)
            .filter(tab => !DV.isUndefined(tab))
            .map((tab, index) => (
              <div key={index} className={classNames('tab-pane', { 'active': active === index })}>{tab}</div>
            ))}
        </div>
      )}
    </div>
  );
};

BaseTabsWithDropDown.defaultProps = {
  className: null,
};

BaseTabsWithDropDown.propTypes = {
  nav:       PropTypes.array,
  active:    PropTypes.number,
  className: PropTypes.string,
  size:      PropTypes.string,
  changeTab: PropTypes.func,
  button:    PropTypes.object,
  dropdown:  PropTypes.shape({
    selected:         PropTypes.number,
    options:          PropTypes.array.isRequired,
    valueKey:         PropTypes.string,
    labelKey:         PropTypes.string,
    onChangeCallback: PropTypes.func,
  }).isRequired,
};

export default BaseTabsWithDropDown;